import { Icon as SharedIcon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import {
  DEFAULT_EHR_TRIAL_DASHBOARD,
  EhrTrialDashboardContent,
} from '@chiroup/core/types/EhrTrialDashboard.type';
import { TrialHomeContent } from '@chiroup/core/types/TrialHome.type';
import {
  DEFAULT_UNSURE_TRIAL_DASHBOARD,
  UnsureTrialDashboardContent,
} from '@chiroup/core/types/UnsureTrialDashboard.type';
import {
  CalendarDaysIcon,
  ChartColumnBigIcon,
  CircleCheckIcon,
  CircleDollarSignIcon,
  HeartIcon,
  MonitorIcon,
  PlayCircleIcon,
  PlugIcon,
  SquarePenIcon,
  UserRoundIcon,
  UserRoundPlusIcon,
} from 'lucide-react';
import React from 'react';
import { TRIAL_CARD_ICON_COMPONENTS, TRIAL_CARD_ICON_FALLBACK } from './cardIcons';

/**
 * Read-only preview of the unsure trial dashboard — the home a trial user sees
 * when their intent is `unsure` (or they dismissed the intent modal). Shown
 * instead of the card-home preview while an unsure-dashboard section is
 * selected in the editor.
 *
 * Two things marketing can only see from here: the three clinical-tool cards
 * come from the Essentials cards edited elsewhere in this same editor, and the
 * workflow's videos and report list come from the EHR dashboard's section. A
 * step with neither configured shows as Coming soon, exactly as it will live.
 *
 * Styling tracks UnsureTrialHome.tsx so "what marketing sees here" matches
 * "what users see live".
 */

type Props = {
  data: TrialHomeContent;
};

type Accent = 'primary' | 'orange' | 'blue';

const TILE: Record<Accent, string> = {
  primary: 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300',
  orange: 'bg-orange-50 text-orange-500 dark:bg-orange-900/30 dark:text-orange-400',
  blue: 'bg-blue-50 text-blue-500 dark:bg-blue-900/30 dark:text-blue-300',
};

const CTA: Record<Accent, string> = {
  primary: 'text-primary-700 dark:text-primary-300',
  orange: 'text-orange-600 dark:text-orange-400',
  blue: 'text-blue-600 dark:text-blue-300',
};

const UnsureTrialHomePreview: React.FC<Props> = ({ data }) => {
  const content: UnsureTrialDashboardContent =
    data.unsureDashboard ?? DEFAULT_UNSURE_TRIAL_DASHBOARD;
  // The workflow reads the EHR dashboard's block on purpose — same experiences,
  // configured once. Editing them is done under "EHR dashboard" in the sidebar.
  const ehr: EhrTrialDashboardContent = data.ehrDashboard ?? DEFAULT_EHR_TRIAL_DASHBOARD;
  const toolCards = data.cards.filter(c => c.enabled && c.section === 'essentials');

  const steps = [
    {
      title: content.schedulingTitle,
      cta: content.schedulingCta,
      icon: CalendarDaysIcon,
      accent: 'primary' as Accent,
      blocked: !ehr.scheduleVideoUrl,
    },
    {
      title: content.chartingTitle,
      cta: content.chartingCta,
      icon: SquarePenIcon,
      accent: 'orange' as Accent,
      blocked: false,
    },
    {
      title: content.billingTitle,
      cta: content.billingCta,
      icon: CircleDollarSignIcon,
      accent: 'orange' as Accent,
      blocked: !ehr.billingVideoUrl,
    },
    {
      title: content.reportingTitle,
      cta: content.reportingCta,
      icon: ChartColumnBigIcon,
      accent: 'blue' as Accent,
      blocked: !ehr.reportingItems.length,
    },
  ];

  return (
    <div className="min-h-full bg-white pb-8 text-left dark:bg-dark-gray-900">
      <div className="px-5 pb-2 pt-5">
        <h1 className="text-xl font-bold text-gray-900 dark:text-dark-gray-100">Hello, Doctor</h1>
        <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:border-primary-900/50 dark:bg-primary-900/20 dark:text-primary-300">
          <SharedIcon icon={Icons.sparkles} className="h-3 w-3" />
          Trial · 14 days left
        </span>
      </div>

      <div className="px-4 sm:px-5">
        {/* Introduction */}
        <div className="mt-4 flex items-start gap-4">
          <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center text-primary-600 dark:text-primary-300">
            <UserRoundIcon className="h-7 w-7" strokeWidth={1.75} />
            <HeartIcon
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-white dark:bg-dark-gray-900"
              strokeWidth={2}
            />
          </span>
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-dark-gray-100">
              {content.introHeading}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-dark-gray-300">
              {content.introCopy}
            </p>
          </div>
        </div>

        {/* Clinical tools */}
        <div className="mt-4 rounded-xl border border-primary-300 bg-primary-200/50 p-4 dark:border-primary-900/60 dark:bg-primary-900/20">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-white">
                <PlugIcon className="h-4 w-4" strokeWidth={2} />
              </span>
              <h2 className="mt-3 text-sm font-bold leading-snug text-gray-900 dark:text-dark-gray-100">
                {content.toolsHeading}
              </h2>
              <p className="mt-3 text-[11px] leading-relaxed text-gray-500 dark:text-dark-gray-300">
                {content.toolsSupportingCopy}
              </p>
            </div>

            {toolCards.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:col-span-3">
                {toolCards.map(card => {
                  const CardIcon =
                    TRIAL_CARD_ICON_COMPONENTS[card.icon] ?? TRIAL_CARD_ICON_FALLBACK;

                  return (
                    <div
                      key={card.id}
                      className="flex min-h-[10rem] flex-col rounded-lg border border-gray-200/80 bg-white p-3.5 dark:border-dark-gray-700 dark:bg-dark-gray-800"
                    >
                      <CardIcon className="h-5 w-5 text-primary-600 dark:text-primary-300" />
                      <p className="mt-3 text-xs font-bold text-gray-900 dark:text-dark-gray-100">
                        {card.title}
                      </p>
                      <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500 dark:text-dark-gray-300">
                        {card.description}
                      </p>
                      <span className="mt-auto inline-flex items-center gap-1.5 self-start rounded bg-primary-600 px-2.5 py-1.5 text-[10px] font-semibold text-white">
                        {card.ctaLabel || content.toolsCta} &rarr;
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="md:col-span-3">
                <p className="text-[11px] italic text-gray-500 dark:text-dark-gray-400">
                  No Essentials cards are enabled. This section renders the same cards as the
                  card-based trial home, edited under "Essentials feature cards".
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Complete EHR */}
        <div className="mt-4 rounded-xl border border-orange-200 bg-orange-100/60 p-4 dark:border-orange-900/60 dark:bg-orange-900/20">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 text-white">
                <MonitorIcon className="h-4 w-4" strokeWidth={2} />
              </span>
              <h2 className="mt-3 text-sm font-bold leading-snug text-gray-900 dark:text-dark-gray-100">
                {content.ehrHeading}
              </h2>
              <p className="mt-1.5 text-[11px] font-medium text-orange-500 dark:text-orange-400">
                {content.ehrSupportingHeadline}
              </p>
              <ul className="mt-4 space-y-1.5">
                {content.ehrBenefits.map(benefit => (
                  <li key={benefit} className="text-[11px] text-gray-500 dark:text-dark-gray-300">
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <ul className="grid grid-cols-2 gap-x-3 gap-y-5 md:flex md:items-start md:gap-0">
                {steps.map((step, idx) => (
                  <li key={step.title} className="flex items-stretch md:min-w-0 md:flex-1">
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span
                        className={[
                          'inline-flex h-9 w-9 items-center justify-center rounded-lg',
                          TILE[step.accent],
                        ].join(' ')}
                      >
                        <step.icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <p className="mt-2 text-[11px] font-semibold leading-snug text-gray-900 dark:text-dark-gray-100">
                        {step.title}
                      </p>
                      {step.blocked ? (
                        <p className="mt-auto pt-0.5 text-[10px] font-medium text-gray-400 dark:text-dark-gray-400">
                          Coming soon
                        </p>
                      ) : (
                        <p
                          className={[
                            'mt-auto pt-0.5 text-[10px] font-semibold',
                            CTA[step.accent],
                          ].join(' ')}
                        >
                          {step.cta} &rarr;
                        </p>
                      )}
                    </div>
                    {idx < steps.length - 1 && (
                      <span
                        aria-hidden
                        className="mt-4 hidden w-8 shrink-0 items-start gap-1 self-start text-gray-300 dark:text-dark-gray-600 md:flex"
                      >
                        <span className="h-px flex-1 border-t border-dashed border-current" />
                        <span className="text-[10px] leading-none">&rarr;</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-col gap-4 rounded-lg bg-white p-3 dark:bg-dark-gray-800 sm:flex-row sm:items-center">
                <div className="flex w-28 shrink-0 overflow-hidden rounded-md border border-gray-200 dark:border-dark-gray-700">
                  <div className="flex w-5 shrink-0 flex-col items-center gap-1.5 bg-primary-700 py-2 dark:bg-primary-900">
                    {[0, 1, 2, 3].map(i => (
                      <span key={i} className="h-1 w-1 rounded-full bg-white/70" />
                    ))}
                  </div>
                  <div className="flex-1 bg-white px-2 py-2 dark:bg-dark-gray-800">
                    <div className="h-1 w-2/3 rounded-full bg-gray-100 dark:bg-dark-gray-700" />
                    <div className="mt-1.5 space-y-1">
                      {['S', 'A', 'O', 'P'].map(letter => (
                        <div key={letter} className="flex items-center gap-1">
                          <span className="w-1.5 text-[8px] font-bold text-gray-700 dark:text-dark-gray-100">
                            {letter}
                          </span>
                          <span className="h-0.5 flex-1 rounded-full bg-gray-100 dark:bg-dark-gray-700" />
                          <CircleCheckIcon className="h-2 w-2 shrink-0 text-primary-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900 dark:text-dark-gray-100">
                    {content.saopHeading}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-dark-gray-300">
                    {content.saopCopy}
                  </p>
                  <span className="mt-2.5 inline-flex flex-wrap items-center gap-2.5">
                    <span className="inline-flex rounded bg-orange-500 px-2.5 py-1 text-[10px] font-semibold text-white">
                      {content.saopCta} &rarr;
                    </span>
                    {/* Mirrors the live page: Why SAOP? shows only once the
                        EHR dashboard's SAOP clip is configured. */}
                    {ehr.saopVideoUrl && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-700 dark:text-dark-gray-200">
                        <PlayCircleIcon className="h-3 w-3 text-orange-500" />
                        {content.whySaopCta}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Walkthrough */}
        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-100/50 p-4 dark:border-blue-900/60 dark:bg-blue-900/20">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <UserRoundPlusIcon
                className="h-8 w-8 shrink-0 text-blue-500 dark:text-blue-300"
                strokeWidth={1.75}
              />
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-dark-gray-100">
                  {content.walkthroughHeading}
                </p>
                <p className="mt-1 max-w-md text-[11px] leading-relaxed text-gray-500 dark:text-dark-gray-300">
                  {content.walkthroughCopy}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded border border-blue-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-700 dark:border-blue-900/50 dark:bg-dark-gray-800 dark:text-blue-300">
              <CalendarDaysIcon className="h-3.5 w-3.5" strokeWidth={2} />
              {content.walkthroughCta} &rarr;
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsureTrialHomePreview;
